import { useEffect, useMemo, useState } from "react";
import { Building2, Plus, X, Search } from "lucide-react";
import { GenericEntityWorkspacePage } from "./shared/GenericEntityWorkspacePage";
import { ambassadorBoardConfig, type EntityListItem } from "./shared/entityWorkspaceBoardConfigs";
import { createAmbassadorOrg, listAmbassadorOrgs, updateAmbassadorOrg, listAdminUsers, addUserToAmbassador, ROLE_OPTIONS } from "../../api/admin";
import { Modal } from "../primitives/Modal";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { Button } from "../primitives/Button";
import { EntityHeaderPattern } from "../patterns/EntityHeaderPattern";
import { AmbassadorDashboard } from "../components/AmbassadorDashboard";
import { COUNTRIES, getProvincesForCountry } from "../utils/countries";
import { getRoleDescription } from "../../utils/permissions";
import { cn } from "../utils/cn";

export function AmbassadorWorkspacePage({ userName = "Utilisateur" }: { userName?: string }) {
  const [ambassadorOrgs, setAmbassadorOrgs] = useState<
    { id: string; name: string; city?: string; status?: string; email?: string; phone?: string; province?: string }[]
  >([]);
  const [items, setItems] = useState<EntityListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "QC - Québec",
    postalCode: "",
    country: "CA",
  });
  const [users, setUsers] = useState<Array<{ id: string; email: string; first_name?: string; last_name?: string; display_name?: string }>>([]);
  const [selectedUsers, setSelectedUsers] = useState<Array<{ userId: string; role: string }>>([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const mapAmbassadorToItem = (org: {
    id: string;
    name: string;
    city?: string;
    status?: string;
  }): EntityListItem => ({
    id: org.id,
    title: org.name,
    subtitle: org.city || "—",
    info: "",
    icon: <Building2 size={16} strokeWidth={1.5} />,
    status: {
      label: org.status === "active" ? "Actif" : "Suspendu",
      tone: org.status === "active" ? "success" : "danger",
    },
  });

  const availableProvinces = useMemo(() => {
    return getProvincesForCountry(form.country);
  }, [form.country]);

  const provinceOptions = useMemo(() => {
    return availableProvinces.map((p) => `${p.code} - ${p.name}`);
  }, [availableProvinces]);

  const countryOptions = useMemo(() => {
    return COUNTRIES.map((c) => `${c.code} - ${c.name}`);
  }, []);

  const availableUsers = useMemo(() => {
    return users.filter((user) => !selectedUsers.some((su) => su.userId === user.id));
  }, [users, selectedUsers]);

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return availableUsers;
    const query = userSearchQuery.toLowerCase();
    return availableUsers.filter(
      (user) =>
        user.email.toLowerCase().includes(query) ||
        user.first_name?.toLowerCase().includes(query) ||
        user.last_name?.toLowerCase().includes(query) ||
        user.display_name?.toLowerCase().includes(query)
    );
  }, [availableUsers, userSearchQuery]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    listAmbassadorOrgs()
      .then((data) => {
        if (!mounted) return;
        const orgs = (data.items || []).filter((org: { type?: string }) => org.type === "ambassador_org");
        setAmbassadorOrgs(orgs);
        const mapped = orgs.map(mapAmbassadorToItem);
        if (mapped.length) {
          setItems(mapped);
          setSelectedId(mapped[0].id);
        } else {
          setItems([]);
          setSelectedId(null);
        }
        setIsLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (modalOpen) {
      listAdminUsers()
        .then((data) => {
          setUsers(data.items || []);
        })
        .catch(() => undefined);
    }
  }, [modalOpen]);

  const selectedOrg = useMemo(() => {
    if (!selectedId) return null;
    const org = ambassadorOrgs.find((org) => org.id === selectedId);
    // Vérifier que l'org existe et est bien de type ambassador_org uniquement
    if (!org || org.type !== "ambassador_org") return null;
    return org;
  }, [ambassadorOrgs, selectedId]);

  const handleCreate = async () => {
    // Validation des champs requis
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom de l'ambassadeur est requis";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setSaving(true);
    try {
      const provinceCode = form.province.split(" - ")[0] || form.province;
      const created = await createAmbassadorOrg({
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        city: form.city,
        province: provinceCode,
        postal_code: form.postalCode,
        country: form.country,
      });

      if (selectedUsers.length > 0) {
        await Promise.allSettled(
          selectedUsers.map((selectedUser) =>
            addUserToAmbassador(created.id, selectedUser.userId, selectedUser.role)
          )
        );
      }

      setAmbassadorOrgs((prev) => [...prev, created]);
      setItems((prev) => [...prev, mapAmbassadorToItem(created)]);
      setSelectedId(created.id);

      setModalOpen(false);
      setForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        province: "QC - Québec",
        postalCode: "",
        country: "CA",
      });
      setSelectedUsers([]);
      setUserSearchQuery("");
      setShowUserDropdown(false);
      setErrors({});
    } catch (error) {
      console.error("Failed to create ambassador:", error);
      alert("Erreur lors de la création de l'ambassadeur. Veuillez réessayer.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      province: "QC - Québec",
      postalCode: "",
      country: "CA",
    });
    setSelectedUsers([]);
    setUserSearchQuery("");
    setShowUserDropdown(false);
    setErrors({});
    setModalOpen(false);
  };

  const handleCountryChange = (value: string) => {
    const countryCode = value.split(" - ")[0] || value;
    const newProvinces = getProvincesForCountry(countryCode);
    const defaultProvince = newProvinces.length > 0 ? `${newProvinces[0].code} - ${newProvinces[0].name}` : "";
    setForm({
      ...form,
      country: countryCode,
      province: defaultProvince,
    });
  };

  const handleAddUser = (userId: string) => {
    if (selectedUsers.some((u) => u.userId === userId)) return;
    setSelectedUsers([...selectedUsers, { userId, role: "ops" }]);
    setUserSearchQuery("");
    setShowUserDropdown(false);
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter((u) => u.userId !== userId));
  };

  const handleUserRoleChange = (userId: string, role: string) => {
    setSelectedUsers(selectedUsers.map((u) => (u.userId === userId ? { ...u, role } : u)));
  };

  const handleEdit = async () => {
    if (!selectedOrg) return;
    const updated = await updateAmbassadorOrg(selectedOrg.id, form);
    setAmbassadorOrgs((prev) => prev.map((org) => (org.id === updated.id ? updated : org)));
    setItems((prev) =>
      prev.map((item) =>
        item.id === updated.id
          ? {
              ...item,
              title: updated.name,
              subtitle: updated.city || "—",
              status: {
                label: updated.status === "active" ? "Actif" : "Suspendu",
                tone: updated.status === "active" ? "success" : "danger",
              },
            }
          : item
      )
    );
    setEditOpen(false);
  };

  const handleToggleStatus = async () => {
    if (!selectedOrg) return;
    const nextStatus = selectedOrg.status === "active" ? "suspended" : "active";
    const updated = await updateAmbassadorOrg(selectedOrg.id, { status: nextStatus });
    setAmbassadorOrgs((prev) => prev.map((org) => (org.id === updated.id ? updated : org)));
    setItems((prev) =>
      prev.map((item) =>
        item.id === updated.id
          ? {
              ...item,
              status: {
                label: updated.status === "active" ? "Actif" : "Suspendu",
                tone: updated.status === "active" ? "success" : "danger",
              },
            }
          : item
      )
    );
  };

  return (
    <>
      <GenericEntityWorkspacePage
        userName={userName}
        config={{
          ...ambassadorBoardConfig,
          entities: [], // Ne pas utiliser les entités mockées
          headerSlot:
            selectedOrg ? (
              <EntityHeaderPattern
                title={selectedOrg.name}
                metaItems={[
                  { label: "Statut", value: selectedOrg.status ?? "Actif", tone: "default" },
                  { label: "Région", value: selectedOrg.city ?? "—", tone: "muted" },
                ]}
                status={{
                  label: selectedOrg.status === "active" ? "Actif" : "Suspendu",
                  tone: selectedOrg.status === "active" ? "success" : "warning",
                }}
                primaryAction={{
                  label: "Modifier",
                  onClick: () => {
                    setForm({
                      name: selectedOrg.name,
                      email: selectedOrg.email || "",
                      phone: selectedOrg.phone || "",
                      city: selectedOrg.city || "",
                      province: selectedOrg.province || "",
                      address: selectedOrg.address_line1 || "",
                      postalCode: selectedOrg.postal_code || "",
                      country: selectedOrg.country || "CA",
                    });
                    setEditOpen(true);
                  },
                }}
                secondaryActions={[
                  {
                    label: selectedOrg.status === "active" ? "Désactiver" : "Réactiver",
                    onClick: handleToggleStatus,
                  },
                ]}
              />
            ) : null,
        }}
        items={items}
        selectedId={selectedId}
        onSelect={(id) => setSelectedId(id)}
        addActionLabel="+ Ajouter"
        onAddAction={() => setModalOpen(true)}
        customContent={
          selectedOrg ? (
            <AmbassadorDashboard
              ambassadorId={selectedOrg.id}
              onManageAccess={() => {
                // TODO: Open membership management modal
                alert("Gestion des accès à implémenter");
              }}
            />
          ) : (
            <div className="rounded-md border border-border bg-surface p-6">
              <div className="text-sm text-muted">Sélectionnez un ambassadeur pour voir le dashboard</div>
            </div>
          )
        }
      />
      <Modal title="Créer un ambassadeur" open={modalOpen} onClose={handleCancel} showDefaultButtons={false} showCloseButton={true} size="xl">
        <div className="space-y-4">
          <div>
            <Input 
              label="Nom de l'ambassadeur" 
              placeholder="Ambassadeur ABC" 
              value={form.name} 
              onChange={(event) => {
                setForm({ ...form, name: event.target.value });
                if (errors.name) {
                  setErrors({ ...errors, name: "" });
                }
              }}
              state={errors.name ? "error" : "default"}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-danger">{errors.name}</p>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
          <Input label="Email" placeholder="email@entreprise.com" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
          <Input label="Téléphone" placeholder="(xxx) xxx-xxxx" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          </div>
          <Input label="Adresse" placeholder="123 rue Principale" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
          <div className="grid gap-3 md:grid-cols-2">
          <Input label="Ville" placeholder="Ville" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
            <Input label="Code postal" placeholder="A1A 1A1" value={form.postalCode} onChange={(event) => setForm({ ...form, postalCode: event.target.value })} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Select
              label="Pays"
              options={countryOptions}
              value={`${form.country} - ${COUNTRIES.find((c) => c.code === form.country)?.name || ""}`}
              onChange={handleCountryChange}
            />
            <Select
              label="Province/État"
              options={provinceOptions}
              value={form.province}
              onChange={(value) => setForm({ ...form, province: value })}
            />
          </div>

          {/* Section Utilisateurs */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-text">Équipe</label>
              <span className="text-xs text-muted">{selectedUsers.length} utilisateur(s) ajouté(s)</span>
            </div>
            <div className="space-y-2">
              {/* Recherche d'utilisateur */}
              <div className="relative">
                <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={userSearchQuery}
                  onChange={(e) => {
                    setUserSearchQuery(e.target.value);
                    setShowUserDropdown(true);
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-surface text-sm text-text placeholder:text-muted"
                />
                {showUserDropdown && (
                  <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-surface shadow-lg">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleAddUser(user.id)}
                          className="w-full text-left px-3 py-2 text-sm transition hover:bg-surface-muted border-b border-border last:border-b-0"
                        >
                          <div className="font-medium text-text">
                            {user.display_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email}
                          </div>
                          <div className="text-xs text-muted">{user.email}</div>
                        </button>
                      ))
                    ) : userSearchQuery ? (
                      <div className="p-3 text-sm text-muted text-center">
                        Aucun utilisateur trouvé
                      </div>
                    ) : (
                      <div className="p-3 text-sm text-muted text-center">
                        Tous les utilisateurs ont été ajoutés
                      </div>
                    )}
                  </div>
                )}
              </div>
              {showUserDropdown && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserDropdown(false)}
                />
              )}

              {/* Liste des utilisateurs sélectionnés */}
              {selectedUsers.length > 0 && (
                <div className="space-y-2">
                  {selectedUsers.map((selectedUser) => {
                    const user = users.find((u) => u.id === selectedUser.userId);
                    if (!user) return null;
                    return (
                      <div key={selectedUser.userId} className="flex items-center gap-2 p-2 rounded-md border border-border bg-surface-muted">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-text">
                            {user.display_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email}
                          </div>
                          <div className="text-xs text-muted">{user.email}</div>
                        </div>
                        <Select
                          options={ROLE_OPTIONS.map((r) => getRoleDescription(r).label)}
                          value={getRoleDescription(selectedUser.role).label}
                          onChange={(label) => {
                            const roleValue = ROLE_OPTIONS.find((r) => getRoleDescription(r).label === label);
                            if (roleValue) handleUserRoleChange(selectedUser.userId, roleValue);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(selectedUser.userId)}
                          className="p-1 rounded-md hover:bg-surface transition"
                        >
                          <X size={16} strokeWidth={1.5} className="text-muted" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button label="Annuler" intent="default" size="sm" onClick={handleCancel} disabled={saving} />
            <Button label="Créer" intent="primary" size="sm" onClick={handleCreate} disabled={saving || !form.name.trim()} />
          </div>
        </div>
      </Modal>
      <Modal title="Modifier l'ambassadeur" open={editOpen} onClose={() => setEditOpen(false)}>
        <div className="space-y-3">
          <Input label="Nom" placeholder="Nom de l'ambassadeur" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          <Input label="Email" placeholder="email@entreprise.com" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
          <Input label="Téléphone" placeholder="(xxx) xxx-xxxx" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          <Input label="Ville" placeholder="Ville" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
          <Input label="Province" placeholder="QC" value={form.province} onChange={(event) => setForm({ ...form, province: event.target.value })} />
          <div className="flex justify-end">
            <Button label="Enregistrer" intent="primary" size="sm" onClick={handleEdit} />
          </div>
        </div>
      </Modal>
    </>
  );
}
